import { z } from "zod";
import { professionTypeSchema } from "./utils";

/**
 * The two logs, on the wire.
 *
 * A TypeScript-only contract, like `studyAnalysis.ts`: the scoring service
 * neither builds nor reads one, so nothing here is emitted to
 * `services/analysis/contracts/` and reshaping it touches no Python.
 */

/**
 * The models the audit trail records, as a SHARED vocabulary.
 *
 * It lives here rather than only in the API because a client has to render
 * these: a raw `PatientStudy` on a clinician's screen is a schema name where a
 * clinical word belongs. The dashboard keys one label per member off this list
 * and `i18n/catalogues.test.ts` asserts the two stay in step, the same way it
 * does for `ErrorCode` and the analysis enums.
 *
 * The API imports it and re-checks every name against Prisma's own `ModelName`
 * (`satisfies readonly ModelName[]` in `auditPolicy.ts`), so a typo here is
 * still a compile error there rather than a silently unaudited table.
 */
export const AUDITED_MODELS = [
  "User",
  "Patient",
  "Study",
  "StudyMember",
  "Survey",
  "Domain",
  "Question",
  "Option",
  "SurveyPlan",
  "Schedule",
  "PatientStudy",
  "SurveyResponse",
  "SurveyResponseAnswer",
  "SurveyTrigger",
  "Hospital",
  "Address",
] as const;

export const auditedModelSchema = z.enum(AUDITED_MODELS);
export type AuditedModel = z.infer<typeof auditedModelSchema>;

export const auditActionSchema = z.enum([
  "CREATE",
  "UPDATE",
  "DELETE",
  "EXECUTE",
]);
export type AuditAction = z.infer<typeof auditActionSchema>;

export const auditActorKindSchema = z.enum([
  "USER",
  "PATIENT",
  "SYSTEM",
  "ANONYMOUS",
]);
export type AuditActorKind = z.infer<typeof auditActorKindSchema>;

/** Who did it, as both logs record it. */
export const auditActorSchema = z.object({
  kind: auditActorKindSchema,
  userId: z.string().nullable(),
  /**
   * The staff account's name, resolved AT READ TIME rather than stored on the
   * row. The id is what the trail is authoritative about; this is a
   * convenience for whoever is reading it, so a rename shows the current name
   * against an old row and a hard-deleted account resolves to null while its
   * id still stands. Null for every non-USER actor.
   */
  displayName: z.string().nullable(),
  patientId: z.string().nullable(),
  professionType: professionTypeSchema.nullable(),
  isAdmin: z.boolean(),
  isSuperAdmin: z.boolean(),
  /** The job name, for a SYSTEM actor: the sweep, the seed, a dev script. */
  source: z.string().nullable(),
});

export const auditLogEntrySchema = z.object({
  id: z.string(),
  /**
   * The table's total order, as a STRING. It is a Postgres bigserial, and a
   * JS BigInt has no JSON form.
   */
  seq: z.string(),
  occurredAt: z.iso.datetime(),
  /** Groups every row one transaction wrote. */
  txId: z.string().nullable(),

  tableName: z.string(),
  rowId: z.string(),
  action: auditActionSchema,
  /** EXECUTE only: which declared domain event this is. */
  event: z.string().nullable(),
  changedColumns: z.array(z.string()),
  /**
   * CREATE carries the whole new row in `after`, children included. UPDATE
   * carries ONLY the changed columns on both sides. DELETE carries the whole
   * row in `before`. EXECUTE carries its payload in `after`.
   */
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  /** sha256 per column for values that never travel. See auditPolicy.ts. */
  redactedDigests: z.unknown().nullable(),

  actor: auditActorSchema,
  studyId: z.string().nullable(),
  studyRole: z.enum(["OWNER", "MEMBER"]).nullable(),

  requestId: z.string().nullable(),
  route: z.string().nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  reason: z.string().nullable(),
});
export type AuditLogEntry = z.infer<typeof auditLogEntrySchema>;

export const accessLogEntrySchema = z.object({
  id: z.string(),
  occurredAt: z.iso.datetime(),
  requestId: z.string().nullable(),
  route: z.string(),
  method: z.string(),
  statusCode: z.number().int(),
  durationMs: z.number().int(),
  actor: auditActorSchema,
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  /** Which patients this request touched, not which route it hit. Filled by
   *  the service layer through `recordPatientAccess`. */
  touchedPatientIds: z.array(z.string()),
});
export type AccessLogEntry = z.infer<typeof accessLogEntrySchema>;

/** Paged the way `GET /patients` is, so clients reuse one paging shape. */
export const auditLogPageSchema = z.object({
  entries: z.array(auditLogEntrySchema),
  totalPages: z.number().int(),
  currentPage: z.number().int(),
  total: z.number().int(),
});
export type AuditLogPage = z.infer<typeof auditLogPageSchema>;

export const accessLogPageSchema = z.object({
  entries: z.array(accessLogEntrySchema),
  totalPages: z.number().int(),
  currentPage: z.number().int(),
  total: z.number().int(),
});
export type AccessLogPage = z.infer<typeof accessLogPageSchema>;

const paging = {
  page: z.coerce.number().int().min(0).default(0),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
};

export const auditLogQuerySchema = z.object({
  ...paging,
  tableName: z.string().optional(),
  rowId: z.string().optional(),
  actorUserId: z.string().optional(),
  studyId: z.string().optional(),
  action: auditActionSchema.optional(),
  event: z.string().optional(),
});
export type AuditLogQuery = z.infer<typeof auditLogQuerySchema>;

export const accessLogQuerySchema = z.object({
  ...paging,
  actorUserId: z.string().optional(),
  /** Everyone who read THIS patient, which is the question a DPO asks. */
  patientId: z.string().optional(),
  /** One request, which is also how a row is correlated with the audit trail. */
  requestId: z.string().optional(),
});
export type AccessLogQuery = z.infer<typeof accessLogQuerySchema>;
